import { Thunk } from "@kazoohr/types";

import { doNothing } from "./doNothing";

/**
 * Creates a closure from a function and a set of arguments to that function.
 *
 * Useful in React components when we have unit tested functions that take lots
 * of arguments, but don't want to test the closure itself. Use this for better,
 * clearer, declarative programming!
 *
 * @param fn Subject function
 * @param args The arguments to pass to fn when called
 * @returns Return value of fn(...args)
 */
export const getClosureFor: Thunk =
  (fn, ...args) =>
  () =>
    fn(...args);

/**
 * Like `getClosureFor`, but can accept `undefined` if the function is optional.
 * Will always return a function.
 */
export function maybeGetClosureFor<T extends AnyFunction | undefined>(
  fn?: T,
  ...args: T extends AnyFunction ? Parameters<T> : []
): T extends AnyFunction ? () => ReturnType<T> : () => void {
  const fnAsAny: any = fn;
  const returnValueAsAny: any = fnAsAny
    ? getClosureFor(fnAsAny, ...args)
    : doNothing;
  return returnValueAsAny;
}

type AnyFunction = (...args: any) => any;
